import React, { useCallback, useEffect, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useNavigation } from "@react-navigation/native";
import { getWeekNumber } from "../../utils";
import { TimeSheetState } from "../../__generated__/graphql";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import SimplePageContent from "../../components/SimplePageContent";
import { useMutation, useQuery } from "@apollo/client/react";
import { GET_TIME_SHEET, REJECT_TIME_SHEET } from "../../graphql/timesheet";
import Label, { LabelMode } from "../../ui_atoms/Label";
import ListItem from "../../ui_atoms/ListItem";
import TimeSheetReview from "../../components/TimeSheetReview";
import { useAsyncError } from "../../hooks/application";
import VStack from "../../ui_atoms/VStack";
import TrackingDatesDisplay from "../../components/TrackingDatesDisplay";
import { useSnackbar } from "../../hooks/snackbar";
import Headline from "../../components/Headline";
import Gap from "../../ui_atoms/Gap";
import { useTracking } from "../../hooks/tracking";
import { BodyMediumText } from "../../components/FormattedText";
import { useTheme } from "../../hooks/use-theme";
import ScreenHeader from "../user/ScreenHeader";
import MissionInfoItem from "../../components/MissionInfoItem";
import { useDateFormatter, useTimesheetsRange } from "../../hooks/date";
import { useSetAlert } from "../../hooks/use-set-alert";

type TimesheetReviewProps = StackScreenProps<RootStackParamList, "TimesheetClientReview">;

type ScreenParams = StackScreenProps<RootStackParamList>;

const TimesheetClientReview = ({ route }: TimesheetReviewProps) => {
    const i18n = useI18n();
    const { colors } = useTheme();
    const { setAlert } = useSetAlert();

    const getTimesheetsRange = useTimesheetsRange();

    const navigation = useNavigation<ScreenParams["navigation"]>();
    const { data, error, loading } = useQuery(GET_TIME_SHEET, {
        variables: { timeSheetId: route.params.id || "" },
        skip: !route.params.id,
        fetchPolicy: "network-only",
    });
    const throwError = useAsyncError();

    useEffect(() => {
        if (!data?.getTimeSheet && !loading) {
            navigation.replace("NotFoundScreen", { error: error, goBack: true, isNotFound: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, loading]);

    const [rejectTimesheet] = useMutation(REJECT_TIME_SHEET);
    const timesheet = data?.getTimeSheet;
    const { showSnackbar } = useSnackbar();
    const { trackEvent } = useTracking();
    const handleRejectTimesheet = useCallback(async () => {
        const variables = {
            timeSheetId: route.params.id || "",
        };

        try {
            const result = await rejectTimesheet({
                variables,
            });
            void trackEvent("click_reject_timesheet");
            if (!result.error?.message) {
                showSnackbar({
                    text: i18n.t(`time_sheet.snackbar.reject`),
                    mode: "success",
                });
                navigation.popToTop();
            } else {
                throwError(new Error(result.error.message || "Unkown error"));
            }
        } catch (e) {
            throwError(e as Error);
        }
    }, [route.params.id, rejectTimesheet, showSnackbar, i18n, navigation, throwError, trackEvent]);

    const onRejectTimesheet = useCallback(() => {
        setAlert({
            title: i18n.t("screens.user.timesheet_review.reject_alert.title"),
            message: i18n.t("screens.user.timesheet_review.reject_alert.description"),
            buttons: [
                {
                    text: i18n.t("screens.user.timesheet_review.reject_alert.confirm"),
                    onPress: () => void handleRejectTimesheet(),
                    style: "destructive",
                },
                {
                    text: i18n.t("screens.user.timesheet_review.reject_alert.cancel"),
                    style: "cancel",
                },
            ],
            options: { cancelable: true },
        });
    }, [handleRejectTimesheet, i18n, setAlert]);

    const [_, week] = timesheet ? getWeekNumber(new Date(timesheet.fromDate)) : [null, null];
    const areActionsEnabled = useMemo(
        () => timesheet?.status === TimeSheetState.Submitted,
        [timesheet?.status],
    );
    const formatDate = useDateFormatter();

    const statusLabel = useMemo(() => {
        switch (timesheet?.status) {
            case TimeSheetState.Open:
                return {
                    mode: "warning" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.client_status.open"),
                };
            case TimeSheetState.Submitted:
                return {
                    mode: "info" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.client_status.submitted", {
                        date: formatDate(
                            timesheet?.submittedAt ? new Date(timesheet.submittedAt) : new Date(),
                        ),
                    }),
                };
            case TimeSheetState.Rejected:
                return {
                    mode: "error" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.client_status.rejected", {
                        date: formatDate(
                            timesheet?.rejectedAt ? new Date(timesheet.rejectedAt) : new Date(),
                        ),
                    }),
                };
            case TimeSheetState.Accepted:
                return {
                    mode: "success" as LabelMode,
                    text: i18n.t("screens.user.timesheet_review.client_status.accepted", {
                        date: formatDate(
                            timesheet?.acceptedAt ? new Date(timesheet.acceptedAt) : new Date(),
                        ),
                    }),
                };
            default:
                return {
                    mode: "default" as LabelMode,
                    text: "",
                };
        }
    }, [i18n, timesheet, formatDate]);

    const subLabel = useMemo(() => {
        const missionInfo = timesheet?.missionInfo;
        if (!missionInfo) {
            return i18n.t("loading");
        }
        return i18n.t("time_sheet.nav_sub_title", {
            week,
            range: timesheet ? getTimesheetsRange([timesheet], "long")?.formattedString : "",
        });
    }, [timesheet, week, getTimesheetsRange, i18n]);

    return (
        <>
            <ScreenHeader
                label={i18n.t("time_sheet.nav_title")}
                subLabel={subLabel}
                onLeftAction={() => navigation.goBack()}
                useSmallLabel
            />
            <LoadingBoundary loading={loading && !data} error={error}>
                {timesheet && (
                    <SimplePageContent>
                        <VStack v={1} h={0.5} gap={1}>
                            <Label {...statusLabel} style={{ textAlign: "center" }} />
                        </VStack>
                        <MissionInfoItem missionInfo={timesheet?.missionInfo} />

                        <Gap gap={0.5} />
                        <VStack h={0.5}>
                            <TimeSheetReview
                                hoursBreakdown={timesheet.trackedDurationBreakdown.items}
                                missionMinutesPerWeek={timesheet.contractWeeklyDurationMinutes}
                                isSubmitted={timesheet.status !== TimeSheetState.Open}
                            />
                            {timesheet.trackingDates.length > 0 && (
                                <>
                                    <Gap gap={1} />
                                    <Headline h="2">
                                        {i18n.t("screens.user.timesheet_review.hours_breakdown")}
                                    </Headline>
                                    <TrackingDatesDisplay trackingDates={timesheet.trackingDates} />
                                </>
                            )}
                        </VStack>
                        {areActionsEnabled && (
                            <>
                                <VStack h="2/3" v={1}>
                                    <BodyMediumText style={{ color: colors.greyAccessible }}>
                                        {i18n.t("screens.user.timesheet_review.reject_description")}
                                    </BodyMediumText>
                                </VStack>
                                <ListItem
                                    labels={[
                                        {
                                            text: i18n.t("screens.user.timesheet_review.reject"),
                                            mode: "cta",
                                        },
                                    ]}
                                    rightActionMode="empty"
                                    onPress={onRejectTimesheet}
                                />
                                <Gap gap={2} />
                            </>
                        )}
                    </SimplePageContent>
                )}
            </LoadingBoundary>
        </>
    );
};

export default TimesheetClientReview;
